import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { formatINR, formatIST } from "@/lib/format";
import { PageHeader } from "@/components/Shell";
import { DisputeBadge, DISPUTE_STATUSES, isDisputeClosed } from "@/components/DisputeStatus";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Plus, Settings2 } from "lucide-react";

const selCls = "h-10 w-full rounded-md bg-slate-900/60 border border-slate-700 px-3 text-sm";

export default function AdminDisputes() {
  const [list, setList] = useState(null);
  const [filter, setFilter] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [manageId, setManageId] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/disputes", { params: filter ? { status: filter } : {} }).then((r) => setList(r.data)).catch(() => setList([]));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <PageHeader title="Chargebacks & Disputes" desc="Track disputes, hold or reverse funds, and keep a full audit trail.">
        <Button onClick={() => setCreateOpen(true)} data-testid="open-dispute-btn" className="bg-emerald-500 hover:bg-emerald-400 text-[#08120e] font-semibold"><Plus className="mr-2 h-4 w-4" />Open Dispute</Button>
      </PageHeader>
      <div className="mb-4 w-full sm:w-60">
        <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="dispute-filter" className={selCls}>
          <option value="">All statuses</option>
          {DISPUTE_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>
      {!list ? <div className="grid place-items-center py-20"><Loader2 className="h-8 w-8 animate-spin text-emerald-400" /></div> : (
        <div className="vp-card rounded-xl overflow-hidden"><div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="border-white/5 hover:bg-transparent">
              {["Dispute", "User", "Original Txn", "Disputed", "Held", "Reversed", "Status", "Opened", ""].map((h) => <TableHead key={h} className="text-slate-400">{h}</TableHead>)}
            </TableRow></TableHeader>
            <TableBody>
              {list.length === 0 ? <TableRow><TableCell colSpan={9} className="text-center text-slate-500 py-10">No disputes.</TableCell></TableRow> :
                list.map((d) => (
                  <TableRow key={d.id} className="border-white/5" data-testid={`dispute-row-${d.dispute_id}`}>
                    <TableCell className="font-mono text-xs">{d.dispute_id}</TableCell>
                    <TableCell><div className="text-sm">{d.user.name}</div><div className="text-xs text-slate-500">{d.user.email}</div></TableCell>
                    <TableCell className="font-mono text-xs">{d.txn_id}</TableCell>
                    <TableCell className="font-mono text-rose-300">{formatINR(d.disputed_amount)}</TableCell>
                    <TableCell className="font-mono text-amber-300">{formatINR(d.hold_amount)}</TableCell>
                    <TableCell className="font-mono">{formatINR(d.reversed_amount)}</TableCell>
                    <TableCell><DisputeBadge status={d.status} /></TableCell>
                    <TableCell className="text-xs text-slate-500 font-mono">{formatIST(d.created_at)}</TableCell>
                    <TableCell><Button size="sm" variant="outline" onClick={() => setManageId(d.dispute_id)} data-testid={`manage-dispute-${d.dispute_id}`} className="border-slate-700 bg-transparent hover:bg-white/5"><Settings2 className="h-4 w-4" /></Button></TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div></div>
      )}
      {createOpen && <CreateDispute onClose={() => setCreateOpen(false)} onCreated={(d) => { setCreateOpen(false); load(); setManageId(d.dispute_id); }} />}
      {manageId && <ManageDispute id={manageId} onClose={() => setManageId(null)} onChanged={load} />}
    </div>
  );
}

function Field({ label, children }) {
  return <div><Label className="text-slate-300 text-sm">{label}</Label><div className="mt-1.5">{children}</div></div>;
}

function CreateDispute({ onClose, onCreated }) {
  const [users, setUsers] = useState([]);
  const [uid, setUid] = useState("");
  const [txns, setTxns] = useState([]);
  const [f, setF] = useState({ txn_id: "", disputed_amount: "", reason: "", reference: "", note: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/admin/users", { params: { include_deleted: true } }).then((r) => setUsers(r.data)); }, []);
  useEffect(() => {
    if (!uid) { setTxns([]); return; }
    api.get(`/admin/users/${uid}/transactions`).then((r) => setTxns(r.data.filter((t) => t.type === "deposit" && t.status === "approved")));
  }, [uid]);

  const pickTxn = (id) => { const t = txns.find((x) => x.txn_id === id); setF({ ...f, txn_id: id, disputed_amount: t ? t.amount : "" }); };
  const submit = async () => {
    setBusy(true);
    try { const r = await api.post("/admin/disputes", { ...f, disputed_amount: Number(f.disputed_amount) }); toast.success(`Dispute ${r.data.dispute_id} opened`); onCreated(r.data); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#121a28] border-white/10 max-w-lg">
        <DialogHeader><DialogTitle className="font-display">Open Dispute / Chargeback</DialogTitle>
          <DialogDescription className="text-slate-400">Link to an existing credited transaction. The original record is never modified.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <Field label="User"><select value={uid} onChange={(e) => setUid(e.target.value)} data-testid="dsp-user" className={selCls}>
            <option value="">Select user…</option>{users.map((u) => <option key={u.id} value={u.id}>{u.name} · {u.email}{u.status === "deleted" ? " (deleted)" : ""}</option>)}
          </select></Field>
          <Field label="Original Transaction"><select value={f.txn_id} onChange={(e) => pickTxn(e.target.value)} data-testid="dsp-txn" className={selCls} disabled={!uid}>
            <option value="">{uid && txns.length === 0 ? "No credited deposits" : "Select transaction…"}</option>
            {txns.map((t) => <option key={t.txn_id} value={t.txn_id}>{t.txn_id} · {formatINR(t.amount)} · {formatIST(t.created_at)}</option>)}
          </select></Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Disputed Amount (₹)"><Input type="number" value={f.disputed_amount} onChange={(e) => setF({ ...f, disputed_amount: e.target.value })} data-testid="dsp-amount" className="bg-slate-900/60 border-slate-700" /></Field>
            <Field label="Reference No."><Input value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} data-testid="dsp-ref" className="bg-slate-900/60 border-slate-700" /></Field>
          </div>
          <Field label="Reason (visible to user)"><Input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="dsp-reason" className="bg-slate-900/60 border-slate-700" /></Field>
          <Field label="Internal note (private)"><Textarea value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="dsp-note" className="bg-slate-900/60 border-slate-700" /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-slate-700 bg-transparent">Cancel</Button>
          <Button onClick={submit} disabled={busy || !f.txn_id || !f.disputed_amount || !f.reason.trim()} data-testid="dsp-submit" className="bg-emerald-500 hover:bg-emerald-400 text-[#08120e] font-semibold">Open Dispute</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ManageDispute({ id, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [status, setStatus] = useState("");
  const [msg, setMsg] = useState("");
  const [money, setMoney] = useState({ amount: "", reason: "" });
  const [note, setNote] = useState("");
  const [evidence, setEvidence] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get(`/admin/disputes/${id}`).then((r) => { setD(r.data); setStatus(r.data.status); }), [id]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, ok) => {
    setBusy(true);
    try { const r = await fn(); setD(r.data); setStatus(r.data.status); toast.success(ok); onChanged(); return true; }
    catch (e) { toast.error(apiError(e)); return false; } finally { setBusy(false); }
  };
  const doMoney = async () => {
    const ok = await run(() => api.post(`/admin/disputes/${id}/${confirm}`, { amount: Number(money.amount), reason: money.reason }), `${confirm} recorded`);
    setConfirm(null);
    if (ok) setMoney({ amount: "", reason: "" });
  };

  if (!d) return null;
  const closed = isDisputeClosed(d.status);
  const labels = { hold: "Place Hold", release: "Release Hold", reverse: "Reverse Funds" };

  return (
    <>
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="bg-[#121a28] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="manage-dispute-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-3">{d.dispute_id} <DisputeBadge status={d.status} /></DialogTitle>
            <DialogDescription className="text-slate-400">{d.user.name} · {d.user.email} · Original {d.txn_id}</DialogDescription></DialogHeader>

          <div className="grid grid-cols-3 gap-3 text-sm">
            <Stat label="Disputed" value={formatINR(d.disputed_amount)} cls="text-rose-300" testid="dsp-disputed" />
            <Stat label="On hold" value={formatINR(d.hold_amount)} cls="text-amber-300" testid="dsp-held" />
            <Stat label="Reversed" value={formatINR(d.reversed_amount)} cls="text-slate-200" testid="dsp-reversed" />
          </div>
          {d.original_txn && <div className="text-xs text-slate-500 font-mono">Original: {formatINR(d.original_txn.amount)} · {d.original_txn.status} · UTR {d.original_txn.utr || "—"} · {formatIST(d.original_txn.created_at)}{d.reference ? ` · Ref ${d.reference}` : ""}</div>}
          <div className="text-sm"><span className="text-slate-500">Reason:</span> {d.reason}</div>

          <Section title="Status">
            <div className="grid sm:grid-cols-[1fr_auto] gap-2">
              <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="dsp-status-select" className={selCls}>
                {DISPUTE_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
              <Button disabled={busy} onClick={() => run(() => api.patch(`/admin/disputes/${id}/status`, { status, message: msg }), "Status updated").then((ok) => ok && setMsg(""))} data-testid="dsp-status-save" className="bg-emerald-500 hover:bg-emerald-400 text-[#08120e] font-semibold">Update</Button>
            </div>
            <Input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Message to user (optional, visible to user)" data-testid="dsp-user-msg" className="bg-slate-900/60 border-slate-700" />
          </Section>

          <Section title="Funds (ledger-recorded, requires confirmation)">
            <div className="grid sm:grid-cols-2 gap-2">
              <Input type="number" value={money.amount} onChange={(e) => setMoney({ ...money, amount: e.target.value })} placeholder="Amount ₹" data-testid="dsp-money-amount" className="bg-slate-900/60 border-slate-700" />
              <Input value={money.reason} onChange={(e) => setMoney({ ...money, reason: e.target.value })} placeholder="Reason (required)" data-testid="dsp-money-reason" className="bg-slate-900/60 border-slate-700" />
            </div>
            <div className="flex flex-wrap gap-2">
              {["hold", "release", "reverse"].map((k) => (
                <Button key={k} variant="outline" disabled={busy || !money.amount || !money.reason.trim() || (closed && k !== "release")} onClick={() => setConfirm(k)} data-testid={`dsp-${k}-btn`}
                  className={`bg-transparent ${k === "reverse" ? "border-rose-500/40 text-rose-300 hover:bg-rose-500/10" : k === "hold" ? "border-amber-500/40 text-amber-300 hover:bg-amber-500/10" : "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"}`}>{labels[k]}</Button>
              ))}
            </div>
            <p className="text-xs text-slate-500">Reversal consumes held funds first, so the same money is never deducted twice.</p>
          </Section>

          <Section title="Evidence & internal notes">
            <div className="flex gap-2"><Input value={evidence} onChange={(e) => setEvidence(e.target.value)} placeholder="Evidence detail (visible to user)" data-testid="dsp-evidence-input" className="bg-slate-900/60 border-slate-700" />
              <Button disabled={busy || !evidence.trim()} onClick={() => run(() => api.post(`/admin/disputes/${id}/evidence`, { text: evidence }), "Evidence added").then((ok) => ok && setEvidence(""))} data-testid="dsp-evidence-btn" variant="outline" className="border-slate-700 bg-transparent">Add</Button></div>
            <div className="flex gap-2"><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Private admin note" data-testid="dsp-note-input" className="bg-slate-900/60 border-slate-700" />
              <Button disabled={busy || !note.trim()} onClick={() => run(() => api.post(`/admin/disputes/${id}/note`, { text: note }), "Note added").then((ok) => ok && setNote(""))} data-testid="dsp-note-btn" variant="outline" className="border-slate-700 bg-transparent">Add</Button></div>
          </Section>

          <Section title="Timeline">
            <div className="max-h-56 overflow-y-auto space-y-1.5 text-xs font-mono" data-testid="dsp-timeline">
              {[...(d.history || []).map((h) => ({ ...h, tag: h.kind })), ...(d.evidence || []).map((h) => ({ ...h, tag: "evidence" })),
                ...(d.user_responses || []).map((h) => ({ ...h, tag: "user", by: "user" })), ...(d.admin_notes || []).map((h) => ({ ...h, tag: "private note" }))]
                .sort((a, b) => a.at.localeCompare(b.at)).map((h, i) => (
                  <div key={i} className="text-slate-400"><span className="text-slate-600">{formatIST(h.at)}</span> · <span className="text-emerald-400">[{h.tag}]</span> {h.text} <span className="text-slate-600">— {h.by}</span></div>
                ))}
            </div>
          </Section>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirm} onOpenChange={() => setConfirm(null)}>
        <DialogContent className="bg-[#121a28] border-white/10 max-w-sm">
          <DialogHeader><DialogTitle className="font-display">Confirm: {labels[confirm]}</DialogTitle>
            <DialogDescription className="text-slate-400">{formatINR(money.amount)} for {d.user.name} on dispute {d.dispute_id}. Reason: {money.reason}. This will be recorded in the ledger and audit log.</DialogDescription></DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)} className="border-slate-700 bg-transparent">Cancel</Button>
            <Button onClick={doMoney} disabled={busy} data-testid="dsp-money-confirm" className="bg-emerald-500 hover:bg-emerald-400 text-[#08120e] font-semibold">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Section({ title, children }) {
  return <div className="rounded-lg border border-white/10 p-4 space-y-3"><div className="text-sm font-semibold text-slate-200">{title}</div>{children}</div>;
}

function Stat({ label, value, cls, testid }) {
  return <div className="rounded-lg bg-black/20 border border-white/5 p-3"><div className="text-xs text-slate-500">{label}</div><div className={`font-mono font-semibold ${cls}`} data-testid={testid}>{value}</div></div>;
}
